/**
 * Default dashboard spec for the Trainings module.
 * Used as the initial template when a user opens the trainings dashboard
 * for the first time (before any agent iteration).
 */
export const trainingsDashboardSpec = {
  fetchSpecs: {
    trainings_dashboard: {
      fetch: [
        {
          toolId: 'fetchTrainingsAdvanced',
          args: {
            ids: null,
            accessId: null,
            availableInCatalog: null,
            search: null,
            status: null,
            isMandatory: null,
            onlyAssigned: null,
            onlyCreatedByMe: null,
            withExpiredMemberships: null,
            returnExpiredMemberships: null,
            withCurrentTrainingClasses: null,
          },
        },
        {
          toolId: 'fetchTrainingMembershipMetrics',
          args: {
            ids: null,
            classId: null,
            dueDate: null,
            trainingId: null,
            employeeId: null,
            search: null,
            status: null,
            teamId: null,
            includePerTrainingRows: true,
          },
        },
      ],
      query:
        "SELECT m.training_name AS training_name,\n       t.training_status AS training_status,\n       t.training_isMandatory AS is_mandatory,\n       t.training_catalog AS in_catalog,\n       m.total_memberships AS total_enrollments,\n       m.unique_employees AS unique_people,\n       m.completed AS completed,\n       m.started AS started,\n       m.partiallycompleted AS partially_completed,\n       m.notstarted AS not_started,\n       m.missing AS missing,\n       m.notassigned AS not_assigned,\n       m.completion_rate_pct AS completion_rate_pct,\n       m.overdue_expired AS overdue_expired,\n       m.no_due_date AS no_due_date\nFROM fetchtrainingmembershipmetrics m\nLEFT JOIN fetchtrainingsadvanced t\n  ON m.training_id = t.training_id\nWHERE m.scope = 'training'",
      columnLabels: {
        training_name: 'Training',
        training_status: 'Estado',
        is_mandatory: 'Obligatorio',
        in_catalog: 'En catálogo',
        total_enrollments: 'Inscripciones (total)',
        unique_people: 'Personas (únicas)',
        completed: 'Completadas',
        started: 'Iniciadas',
        partially_completed: 'Parcialmente completadas',
        not_started: 'No iniciadas',
        missing: 'Faltan',
        not_assigned: 'No asignadas',
        completion_rate_pct: 'Tasa de finalización (%)',
        overdue_expired: 'Caducadas (overdue)',
        no_due_date: 'Sin fecha de caducidad',
      },
    },
  },
  items: [
    {
      id: 'm1',
      title: 'Inscripciones',
      description: 'Total de inscripciones a trainings.',
      explanation: 'Suma del total de **inscripciones** (enrollments) en todos los trainings.',
      type: 'metric',
      format: { type: 'number' },
      computation: {
        datasetId: 'trainings_dashboard',
        aggregation: 'sum',
        column: 'total_enrollments',
      },
      itemHeight: 240,
    },
    {
      id: 'm2',
      title: 'Personas únicas',
      description: 'Total de personas únicas inscritas.',
      explanation:
        'Suma de **personas únicas** por training (una persona puede contar varias veces si está en varios trainings).',
      type: 'metric',
      format: { type: 'number' },
      computation: {
        datasetId: 'trainings_dashboard',
        aggregation: 'sum',
        column: 'unique_people',
      },
      itemHeight: 240,
    },
    {
      id: 'm3',
      title: 'Completadas',
      description: 'Total de inscripciones completadas.',
      explanation: 'Suma de inscripciones con estado **completado**.',
      type: 'metric',
      format: { type: 'number' },
      computation: {
        datasetId: 'trainings_dashboard',
        aggregation: 'sum',
        column: 'completed',
      },
      itemHeight: 240,
    },
    {
      id: 'm4',
      title: 'Tasa finalización',
      description: 'Tasa media de finalización por training.',
      explanation:
        'Media simple de la **tasa de finalización (%)** entre trainings (no ponderada por inscripciones).',
      type: 'metric',
      format: { type: 'percent' },
      decimals: 1,
      computation: {
        datasetId: 'trainings_dashboard',
        aggregation: 'avg',
        column: 'completion_rate_pct',
      },
      itemHeight: 240,
    },
    {
      id: 'c1',
      title: 'Inscripciones por training',
      description: 'Inscripciones (total) por training (top 10).',
      explanation: 'Suma de **inscripciones** por training, ordenado de mayor a menor.',
      type: 'chart',
      chart: {
        type: 'bar',
        orientation: 'horizontal',
        valueFormat: { type: 'number' },
        datasetId: 'trainings_dashboard',
        xAxis: 'training_name',
        yAxis: 'total_enrollments',
        aggregation: 'sum',
        sortBy: 'value',
        sortOrder: 'desc',
        limit: 10,
      },
      itemHeight: 336,
    },
    {
      id: 'c2',
      title: 'Finalización por training',
      description: 'Tasa de finalización (%) por training (top 15).',
      explanation: 'Media de la **tasa de finalización (%)** por training.',
      type: 'chart',
      chart: {
        type: 'bar',
        orientation: 'horizontal',
        valueFormat: { type: 'percent' },
        datasetId: 'trainings_dashboard',
        xAxis: 'training_name',
        yAxis: 'completion_rate_pct',
        aggregation: 'avg',
        sortBy: 'value',
        sortOrder: 'desc',
        limit: 15,
      },
      itemHeight: 336,
    },
    {
      id: 't1',
      title: 'Detalle por training',
      description: 'Tabla con métricas por training.',
      explanation: 'Detalle por training: inscripciones, estados y caducidades.',
      type: 'collection',
      columns: [
        { id: 'training_name', label: 'Training', width: 260 },
        { id: 'training_status', label: 'Estado', width: 110 },
        { id: 'is_mandatory', label: 'Obligatorio', width: 110 },
        { id: 'in_catalog', label: 'En catálogo', width: 110 },
        { id: 'total_enrollments', label: 'Inscripciones (total)', width: 150 },
        { id: 'unique_people', label: 'Personas (únicas)', width: 150 },
        { id: 'completed', label: 'Completadas', width: 120 },
        {
          id: 'partially_completed',
          label: 'Parcialmente completadas',
          width: 200,
        },
        { id: 'not_started', label: 'No iniciadas', width: 120 },
        {
          id: 'completion_rate_pct',
          label: 'Tasa de finalización (%)',
          width: 180,
        },
        { id: 'overdue_expired', label: 'Caducadas (overdue)', width: 170 },
        {
          id: 'no_due_date',
          label: 'Sin fecha de caducidad',
          width: 190,
        },
      ],
      computation: {
        datasetId: 'trainings_dashboard',
        sortBy: 'total_enrollments',
        sortOrder: 'desc',
      },
      itemHeight: 480,
    },
  ],
} as const
